import {skipToken} from '@reduxjs/toolkit/query';
import {
  Card,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  OptionTypeBase,
  Separator,
  showNotification,
} from 'platform/components';
import {Box, Grid, HStack, Show, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil} from 'ramda';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {
  PatchRmaRequestRequest,
  PatchRmaRequestRequestSchema,
  useGetRmaRequestQuery,
  usePatchRmaRequestMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  getOptionsFromAuthorizationProfiles,
  getOptionsFromCustomersAsSupplier,
  handleApiError,
  useGetSupplierCustomersQuery,
  usePermissions,
} from '@dms/shared';
import {ReadOnlyField} from '@dms/shared-aftersales';

import {Nullish, parseDate} from 'shared/web';

import {RMA_REQUEST_STATUS} from '../../../../../constants/RMA_REQUEST_STATUS';
import {UserReadOnlyField} from './UserReadOnlyField';

type RmaRequestHeaderForm = PatchRmaRequestRequest['body'];

const RmaRequestHeaderFormSchema = PatchRmaRequestRequestSchema.shape.body;

const getOptionLabel = (options: OptionTypeBase<string>[], value: string | Nullish) =>
  options.find((option) => option.value === value)?.label;

interface GeneralInformationProps {
  rmaRequestId: string;
}

export function GeneralInformation(props: GeneralInformationProps) {
  const dateTimeFormatter = useDateTimeFormatter();

  const [canEditRmaRequest] = usePermissions({permissionKeys: ['rmaRequestEdit']});

  const rmaRequestQuery = useGetRmaRequestQuery({rmaRequestId: props.rmaRequestId});

  const [patchRmaRequest] = usePatchRmaRequestMutation();

  const tenantQuery = useGetTenantQuery();

  const authorizationProfilesQuery = useGetAuthorizationProfilesQuery(
    isNil(tenantQuery.data) ? skipToken : {'x-tenant': tenantQuery.data.id}
  );

  const suppliersQuery = useGetSupplierCustomersQuery();

  const rmaRequest = rmaRequestQuery.data;

  const isEditable = canEditRmaRequest && rmaRequest?.status === RMA_REQUEST_STATUS.PENDING;

  const supplierOptions = getOptionsFromCustomersAsSupplier(suppliersQuery.data);

  const authorizationProfileOptions = getOptionsFromAuthorizationProfiles(
    authorizationProfilesQuery.data,
    rmaRequest?.authorizationProfileId ?? null
  );

  const formatDateTime = (value: string | Nullish) =>
    isNil(value) ? null : dateTimeFormatter('dateTimeShort', parseDate(value));

  const handleSubmit: FormSubmitHandler<RmaRequestHeaderForm> = (values) =>
    patchRmaRequest({rmaRequestId: props.rmaRequestId, body: values})
      .unwrap()
      .then(() => showNotification.success())
      .catch(handleApiError);

  return (
    <Card title={i18n.t('entity.warehouse.labels.generalInformation')}>
      <DataStatus
        isLoading={
          tenantQuery.isLoading || authorizationProfilesQuery.isLoading || suppliersQuery.isLoading
        }
        isError={
          tenantQuery.isError || authorizationProfilesQuery.isError || suppliersQuery.isError
        }
      >
        <VStack spacing={4}>
          <Show when={isEditable}>
            <Form<RmaRequestHeaderForm>
              experimentalZodSchema={RmaRequestHeaderFormSchema}
              defaultValues={{
                supplierId: rmaRequest?.supplierId ?? '',
                authorizationProfileId: rmaRequest?.authorizationProfileId ?? '',
              }}
              onSubmit={handleSubmit}
            >
              {(control) => (
                <HStack spacing={4} align="flex-end">
                  <Box flex={1}>
                    <Grid columns={2}>
                      <FormField
                        control={control}
                        type="choice"
                        name="supplierId"
                        label={i18n.t('entity.warehouse.labels.supplier')}
                        options={supplierOptions}
                        isRequired
                        isNotClearable
                        menuInPortal
                        data-testid={testIds.warehouse.rmaRequestDetailOverview('supplierId')}
                      />
                      <FormField
                        control={control}
                        type="choice"
                        name="authorizationProfileId"
                        label={i18n.t('entity.warehouse.labels.authorizationProfile')}
                        options={authorizationProfileOptions}
                        isRequired
                        isNotClearable
                        menuInPortal
                        data-testid={testIds.warehouse.rmaRequestDetailOverview(
                          'authorizationProfileId'
                        )}
                      />
                    </Grid>
                  </Box>
                  <FormButton
                    control={control}
                    type="submit"
                    variant="secondary"
                    title={i18n.t('general.actions.saveChanges')}
                  />
                </HStack>
              )}
            </Form>
          </Show>
          <Show when={!isEditable}>
            <Grid columns={4}>
              <ReadOnlyField
                label={i18n.t('entity.warehouse.labels.supplier')}
                value={getOptionLabel(supplierOptions, rmaRequest?.supplierId)}
              />
              <ReadOnlyField
                label={i18n.t('entity.warehouse.labels.authorizationProfile')}
                value={getOptionLabel(
                  authorizationProfileOptions,
                  rmaRequest?.authorizationProfileId
                )}
              />
            </Grid>
          </Show>
          <Separator spacing={0} />
          <Grid columns={4}>
            <ReadOnlyField
              label={i18n.t('entity.warehouse.labels.number')}
              value={rmaRequest?.number}
            />
            <UserReadOnlyField
              label={i18n.t('general.labels.createdBy')}
              userId={rmaRequest?.createdBy}
            />
            <ReadOnlyField
              label={i18n.t('general.labels.createdAt')}
              value={formatDateTime(rmaRequest?.createdAt)}
            />
            <UserReadOnlyField
              label={i18n.t('general.labels.updatedBy')}
              userId={rmaRequest?.modifiedBy}
            />
            <ReadOnlyField
              label={i18n.t('general.labels.updatedAt')}
              value={formatDateTime(rmaRequest?.modifiedAt)}
            />
            <UserReadOnlyField
              label={i18n.t('general.labels.completedBy')}
              userId={rmaRequest?.completedBy}
            />
            <ReadOnlyField
              label={i18n.t('general.labels.completedAt')}
              value={formatDateTime(rmaRequest?.completedAt)}
            />
          </Grid>
        </VStack>
      </DataStatus>
    </Card>
  );
}
